/**
 * Dry-Run Service - SPEC-014 AI Button Writeback
 *
 * Provides preview functionality for AI suggestions without modifying
 * the original document. Generates frontmatter diff for UI display.
 *
 * @module features/ai-button/apply/dry-run
 * @see SPEC-014 Requirements 7.1, 7.2, 7.3, 7.5
 */

import { applyWriteback, extractFrontmatter, extractBody } from '@linkode/shared';
import type { AiWriteback } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Result of dry-run apply operation.
 *
 * @see Requirement 7.1
 */
export interface DryRunResult {
  /** Frontmatter content before apply (YAML only, no delimiters) */
  before: string;
  /** Frontmatter content after apply (YAML only, no delimiters) */
  after: string;
  /** Whether document body remained unchanged */
  bodyUnchanged: boolean;
  /** Warnings from apply operation (e.g., truncation) */
  warnings: string[];
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Perform a dry-run of AI writeback application.
 *
 * This function simulates applying AI suggestions to a markdown document
 * without actually modifying it. It returns a diff of the frontmatter
 * and verifies that the body content would remain unchanged.
 *
 * Key behaviors:
 * - Extracts before/after frontmatter for diff display
 * - Verifies body preservation (Requirement 7.2)
 * - Does not modify original markdown (Requirement 7.3)
 * - Propagates warnings from applyWriteback (Requirement 7.5)
 *
 * @param markdown - Original markdown document
 * @param writeback - AI writeback data to preview
 * @returns Dry-run result with frontmatter diff and body check
 *
 * @example
 * ```typescript
 * const result = dryRunApply('# Hello', { ai_tags: ['tag1'], ai_links: [] });
 * console.log(result.before); // ''
 * console.log(result.after);  // 'ai_tags:\n  - tag1\nai_links: []'
 * console.log(result.bodyUnchanged); // true
 * ```
 *
 * @see SPEC-014 Requirements 7.1, 7.2, 7.3, 7.5
 */
export function dryRunApply(markdown: string, writeback: AiWriteback): DryRunResult {
  // Extract before state (Requirement 7.1)
  const beforeFrontmatter = extractFrontmatter(markdown);

  // Apply writeback to get after state
  const { markdown: newMarkdown, warnings } = applyWriteback(markdown, writeback);

  // Extract after state (Requirement 7.1)
  const afterFrontmatter = extractFrontmatter(newMarkdown);

  // Verify body unchanged (Requirement 7.2)
  const beforeBody = extractBody(markdown);
  const afterBody = extractBody(newMarkdown);

  return {
    before: beforeFrontmatter,
    after: afterFrontmatter,
    bodyUnchanged: beforeBody === afterBody,
    warnings,
  };
}
